import { CheckedIcon, UnCheckedIcon } from '@components/icon/svg';
import { useFetchAssignedTags } from '@hooks/getQueries';
import { Icon } from '@iconify/react';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  ListItemText,
  OutlinedInput,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatInTitleCase } from '@utils/constants';
import { debounce } from 'lodash';
import * as React from 'react';

const ITEM_HEIGHT = 90;
const ITEM_PADDING_TOP = 8;
const VISIBLE_ITEMS = 5;

const MenuProps = {
  PaperProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * VISIBLE_ITEMS + ITEM_PADDING_TOP,
      overflow: 'hidden',
    },
  },
  MenuListProps: {
    sx: {
      maxHeight: ITEM_HEIGHT * VISIBLE_ITEMS + ITEM_PADDING_TOP,
      overflowY: 'auto',
      pb: '10px',
    },
  },
};

export default function TranscriptFilterByTag({
  label = 'Filter by Tags',
  searchPlaceholder = 'Search Tag',
  id,
  onFilterChange,
  selectedFilters = [],
}) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
  const [selectedValuesByTag, setSelectedValuesByTag] = React.useState({});
  const [isFiltering, setIsFiltering] = React.useState(false);

  // Create debounced function to update search query
  const debouncedSetSearchQuery = React.useMemo(
    () =>
      debounce((query) => {
        setDebouncedSearchQuery(query);
      }, 300),
    [],
  );

  // Update debounced search query when searchQuery changes
  React.useEffect(() => {
    debouncedSetSearchQuery(searchQuery);
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [searchQuery, debouncedSetSearchQuery]);

  // Fetch assigned tags with debounced search query
  const { data: assignedTagList, isFetching: isFetchingAssignedTag } = useFetchAssignedTags(
    id,
    debouncedSearchQuery,
  );

  // Synchronize internal state with selectedFilters prop to preserve selected tags
  React.useEffect(() => {
    if (selectedFilters && selectedFilters.length > 0) {
      const newSelectedValuesByTag = {};
      selectedFilters.forEach((filter) => {
        if (filter.tag_key_id && filter.value_key_id) {
          newSelectedValuesByTag[filter.tag_key_id] = Array.isArray(filter.value_key_id)
            ? filter.value_key_id
            : [filter.value_key_id];
        }
      });
      setSelectedValuesByTag(newSelectedValuesByTag);
    } else {
      // Only clear if selectedFilters is explicitly empty array, not undefined
      if (selectedFilters && selectedFilters.length === 0) {
        setSelectedValuesByTag({});
      }
    }
  }, [selectedFilters]);

  // Calculate total selected count
  const totalSelectedCount = React.useMemo(() => {
    return Object.values(selectedValuesByTag).reduce(
      (total, values) => total + (values?.length || 0),
      0,
    );
  }, [selectedValuesByTag]);

  // Handle apply filter
  const handleApply = () => {
    if (!onFilterChange) return;

    setIsFiltering(true);

    // Convert selectedValuesByTag to filter format expected by API
    const filterList = [];
    Object.entries(selectedValuesByTag).forEach(([tagKeyId, valueIds]) => {
      if (valueIds && valueIds.length > 0) {
        const tagData = assignedTagList?.find((tag) => tag.tag_key_id === tagKeyId);
        if (tagData) {
          filterList.push({
            tag_key_id: tagKeyId,
            value_key_id: valueIds,
          });
        }
      }
    });

    onFilterChange(filterList);
    setOpen(false);
    setIsFiltering(false);
  };

  // Handle clear all filters
  const handleClearAll = () => {
    setSelectedValuesByTag({});
    if (onFilterChange) {
      onFilterChange([]);
    }
  };
  return (
    <>
      <FormControl fullWidth>
        <Select
          multiple
          displayEmpty
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          size="small"
          value={[]}
          input={<OutlinedInput />}
          renderValue={() => {
            if (totalSelectedCount === 0) {
              return (
                <Typography fontWeight={'bold'} display={'flex'} alignItems={'center'}>
                  <Icon icon="mdi:filter-outline" width="22" height="22" />
                  {label}
                </Typography>
              );
            }

            return (
              <Box display={'flex'} alignItems={'center'}>
                <Typography display="flex" alignItems="center" fontWeight={'bold'}>
                  <Icon icon="mdi:filter-outline" width="22" height="22" />{' '}
                  {totalSelectedCount > 0 ? `${label} (${totalSelectedCount})` : 'madan'}
                </Typography>
                <Tooltip title="Clear Selection" arrow>
                  <CloseIcon
                    fontSize="small"
                    onMouseDown={(e) => e.stopPropagation()}
                    onClick={handleClearAll}
                    sx={{ cursor: 'pointer', ml: 1, '&:hover': { color: 'error.main' } }}
                  />
                </Tooltip>
              </Box>
            );
          }}
          MenuProps={MenuProps}
        >
          <Box px={1} py={1} onClick={(e) => e.stopPropagation()}>
            <Box display={'flex'} justifyContent={'end'} mb={1} mr={0.3}>
              <Typography color={'#7d5ae8'} sx={{ cursor: 'pointer' }} onClick={handleClearAll}>
                Clear All
              </Typography>
            </Box>
            <TextField
              placeholder={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => {
                e.stopPropagation();
                setSearchQuery(e.target.value);
              }}
              onKeyDown={(e) => e.stopPropagation()}
              size="small"
              fullWidth
            />
          </Box>

          {isFetchingAssignedTag ? (
            <Box display={'flex'} justifyContent={'center'} my={3}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            (!assignedTagList || assignedTagList.length <= 0) && (
              <Typography textAlign="center" p={1}>
                Empty tag
              </Typography>
            )
          )}

          {assignedTagList?.map((option) => (
            <Box key={option.tag_key_id} sx={{ mb: 1 }}>
              <Box display="flex" alignItems="center" ml={1}>
                <Checkbox
                  size="small"
                  sx={{ p: 0, mr: 1 }}
                  indeterminate={
                    (selectedValuesByTag[option.tag_key_id]?.length || 0) > 0 &&
                    (selectedValuesByTag[option.tag_key_id]?.length || 0) < option.tag_values.length
                  }
                  disabled={false}
                  checked={
                    (selectedValuesByTag[option.tag_key_id]?.length || 0) ===
                    option.tag_values.length
                  }
                  onChange={() => {
                    setSelectedValuesByTag((prev) => {
                      const allIds = option.tag_values.map((v) => v.value_id);
                      const currently = prev[option.tag_key_id] || [];
                      const next = currently.length === allIds.length ? [] : allIds;
                      return { ...prev, [option.tag_key_id]: next };
                    });
                  }}
                  checkedIcon={<CheckedIcon />}
                  icon={<UnCheckedIcon />}
                />
                <ListItemText primary={formatInTitleCase(option.tag_key)} />
              </Box>

              <Box sx={{ bgcolor: '#fbfbfb', mx: 1, borderRadius: 1, p: 1 }}>
                {option.tag_values.map((item) => (
                  <Box
                    key={item.tag_value_id}
                    sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}
                  >
                    <Checkbox
                      size="small"
                      sx={{ p: 0, mr: 1 }}
                      checked={
                        selectedValuesByTag[option.tag_key_id]?.includes(item.tag_value_id) || false
                      }
                      onChange={() => {
                        setSelectedValuesByTag((prev) => {
                          const curr = prev[option.tag_key_id] || [];
                          const next = curr.includes(item.tag_value_id)
                            ? curr.filter((v) => v !== item.tag_value_id)
                            : [...curr, item.tag_value_id];
                          return { ...prev, [option.tag_key_id]: next };
                        });
                      }}
                      checkedIcon={<CheckedIcon />}
                      icon={<UnCheckedIcon />}
                    />
                    <Typography variant="body2">{formatInTitleCase(item.value_key)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}

          {/* Sticky footer */}
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              width: '100%',
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
              p: 1,
              zIndex: 1,
            }}
          >
            <Button variant="contained" fullWidth onClick={handleApply} disabled={isFiltering}>
              {isFiltering ? <CircularProgress size={24} /> : 'Apply Filter'}
            </Button>
          </Box>
        </Select>
      </FormControl>
    </>
  );
}
